namespace Solovis.UI.InputControls {

    export interface FormBuilderOptions {
        container: JQuery | HTMLElement | string,

        portalServices?: Solovis.Portal.IPortalServices;

        /**
         * Apply fixed width to label column in pixels.
         */
        labelColumnWidth?: number;

        /**
         * Apply fixed width to the FormBuilder table.
         */
        width?: number;

        /**
         * If form entry does not specify an initialValue property, then the options will fallback to here.
         */
        initialValues?: any;
    }

    export interface FormBuilderShowAsDialogOptions {
        title?: string;

        modal?: boolean;
        desiredWidth?: number;
        desiredHeight?: number;

        ownerDialog?: Solovis.UI.Dialog;
        buttons?: DialogButtonOptions[];

        /**
         * If true, default buttons for save and cancel will be added.         
         */
        showSaveCancel?: boolean;
        saveButtonText?: string;

        /**
         * Callback to call if default "Save" button is clicked. Returns true if dialog should be closed.
         */
        saveCallback?: () => boolean    
    }

    /**
     * FormBuilder will be used to build up and maintain a list of input controls for a form.
     */
    export class FormBuilder {

        private _portalServices: Solovis.Portal.IPortalServices;

        public _options: FormBuilderOptions;
        public _container: JQuery | HTMLElement | string;

        public _initialValues: any;

        public _entryNameToEntryMap: { [entryName: string]: FormEntry };
        public _entryList: FormEntry[];
        public _postSetupCallbacks: any;

        public formValidationModel: Solovis.UI.InputControls.ValidationModel;        

        public _validationDiv: JQuery;
        public _validationModelControl: Solovis.UI.InputControls.ValidationModelControl;

        public _formDiv: JQuery;
        private _desiredWidth: number;


        constructor(options: FormBuilderOptions) {
            if (options.portalServices) {
                this._portalServices = options.portalServices;
            }
            else {
                this._portalServices = new Solovis.Portal.SitePortalServices();
            }

            this._options = options;

            this._container = options.container;
            var containerElement = $(this._container).get(0);
            (containerElement as any).__solovisFormBuilder = this;

            this._initialValues = options.initialValues;

            this._entryNameToEntryMap = {};
            this._entryList = [];

            this._postSetupCallbacks = [];

            // The form validation model allows a global error or success message to be set. This is a public field.
            this.formValidationModel = new Solovis.UI.InputControls.ValidationModel();

            this._desiredWidth = options.width ? options.width : 500;

            this._setupContainer();           
        }

        public portalServices(): Solovis.Portal.IPortalServices {
            return this._portalServices;
        }

        public _setupContainer() {
            this._validationDiv = $("<div>").appendTo(this._container);
            this._validationModelControl = new Solovis.UI.InputControls.ValidationModelControl({
                controlHost: this._validationDiv,
                validationModel: this.formValidationModel
            });
            this._validationModelControl.setAsCurrentControl();

            this._formDiv = $("<div>").addClass("formbuilder-form").appendTo(this._container);
            if (this._desiredWidth) {
                this._formDiv.width(this._desiredWidth);
            }                      
        }


        /**
         * Retrieve FormEntry instance by name. Does not throw exception if entry for name is not found.
         * @param name
         */
        public tryGetEntryByName(name: string): FormEntry {           
            var entry = this._entryNameToEntryMap[name];
            if (entry) {
                return entry;
            }
            else {
                return null;
            }
        }


        /**
         * Retrieve FormEntry instance by name. Throws exception if entry for name is not found.
         * @param name
         */
        public getEntryByName(name: string): FormEntry {          
            var entry = this.tryGetEntryByName(name);
            if (entry) {
                return entry;
            }
            else {
                throw "No form entry found for name: " + name;
            }
        }

        /**
         * Retrieve FormEntry value by name. Throws exception if entry for name is not found.
         * @param name
         */
        public getValueByName(name: string): any {           
            var entry = this.getEntryByName(name);
            var entryValue = entry.value();
            return entryValue;
        }

        /**
         * Retrieve FormEntry value by name. Returns null if entry for name is not found.
         * @param name
         */
        public tryGetValueByName(name: string): any {
            var entry = this.tryGetEntryByName(name);
            if (entry) {
                var entryValue = entry.value();
                return entryValue;
            }
            else {
                return null;
            }
        }

        public getEntryList(): FormEntry[] {
            return this._entryList.slice(0);
        }

        /**
         * Retrieve all entries that support getValue.
         */
        public getValueEntryList(): FormEntry[] {
            return _.filter(this._entryList, function (e: FormEntry) {
                return e.entryEditor && e.entryEditor.getValue;
            });
        }

        public getValues(): any {
            let values: any = {};

            let valueEntries = this.getValueEntryList();
            for (let entry of valueEntries) {
                values[entry.name] = entry.value();
            }

            return values;
        }

        public width(newWidth?: number) {
            if (arguments.length) {
                this._desiredWidth = newWidth;

                if (this._formDiv) {
                    this._formDiv.outerWidth(this._desiredWidth);
                }

                for (let entry of this._entryList) {
                    entry._updateWidth(this._desiredWidth);
                }
            }
            else {
                return this._desiredWidth;
            }
        }

        public setWidth(newWidth: number) {
            this.width(newWidth);
        }

        public addEntry(entryOptions: FormEntryOptions): FormEntry {
            var self = this;

            // Create the FormEntry and add it to our collections.
            var formEntry = new Solovis.UI.InputControls.FormEntry({
                formBuilder: this,
                entryOptions: entryOptions
            });

            this._entryNameToEntryMap[formEntry.name] = formEntry;
            this._entryList.push(formEntry);

            // Repeat valueChanged events from individual formEntry instances, so that it is possible to
            // just listen in one place for those events.
            ($(formEntry) as any).bind("valueChanged", function (e, data) {
                self._notifyValueChanged(data.formEntry);
            });

            // Register postSetup callback if necessary.
            var postSetup = entryOptions.postSetup ? entryOptions.postSetup : null;
            if (postSetup) {
                this._postSetupCallbacks.push(function () {
                    postSetup(formEntry);
                });
            }

            return formEntry;
        }


        /**
         * setupDone should be called after all entries are created so that postSetup callbacks can be called.
         */
        public setupDone() {            
            var self = this;

            if (this._postSetupCallbacks) {
                $(this._postSetupCallbacks).each(function (i, postSetupCallback: any) {
                    postSetupCallback();
                });

                this._postSetupCallbacks = [];
            }

            this._updateVisibilityForAllEntries();
        }


        /**
         * Check all of the entries for errors and return true if at least one entry has an error.
         */
        public hasErrors(): boolean {            
            var errorFound = false;

            for (var i = 0; i < this._entryList.length; i++) {
                var entry = this._entryList[i];
                if (!entry.isValid()) {
                    errorFound = true;
                }
            }

            var hasError = this.formValidationModel.hasError();

            return errorFound || hasError;
        }


        /**
         * Change the visibility of the form (will just show or hide the top-level element).
         * @param visible
         */
        public showEntries(visible) {
            // NOTE: This should not change the visibility of the individual 
            //   rows because we want their individual visibility state to be maintained
            //   when hiding and showing the whole entry table.
            if (this._formDiv) {
                if (visible) {
                    this._formDiv.css("display", "block");
                }
                else {
                    this._formDiv.css("display", "none");
                }
            }
        };


        public focusFirst() {
            if (this._entryList && this._entryList.length > 0) {
                this._entryList[0].focus();
            }
        }


        public _notifyValueChanged(formEntry) {            
            this._updateVisibilityForAllEntries();

            $(this).trigger("valueChanged", { formEntry: formEntry, formBuilder: this });
        }


        public bindValueChanged(callback: (e: any, data: { formEntry: FormEntry, formBuilder: FormBuilder }) => any) {
            $(this).bind("valueChanged", callback as any);
        }


        public _internalRemoveEntryByName(name: string) {
            // Called by FormEntry only when entry removal has been requested.
            this._entryList = _.filter(this._entryList, function (e: FormEntry) { return e.name !== name; });
            delete this._entryNameToEntryMap[name];

            this._notifyValueChanged(null);
        }

        public _updateVisibilityForAllEntries() {
            this._entryList.forEach((entry) => {
                entry._updateVisibility();
            });
        }


        /**
         * errors should be an array of { propertyName: "entryName", message: "error text", (html: "html" optional) }
         * @param errors
         */
        public applyErrors(errors) {           
            var self = this;

            $(errors).each(function (i, error: any) {
                var name = error.propertyName;
                var entry = self.tryGetEntryByName(name);
                if (entry) {
                    entry.validationModel.state({
                        error: error
                    });
                }
                // TODO: CDoise: add code to handle case where there are multiple errors that are not for a known formEntry.
                else {
                    self.formValidationModel.state({
                        error: error
                    });
                }
            });
        }


        public showAsDialog(showDialogOptions?: FormBuilderShowAsDialogOptions): Solovis.UI.Dialog {
            let self = this;

            if (!showDialogOptions) {
                showDialogOptions = {};
            }

            if ($(this._container).parent().length !== 0) {
                throw "FormBuilder container should not have a parent if it is going to be shown as a dialog.";
            }

            let desiredWidth = showDialogOptions.desiredWidth ? showDialogOptions.desiredWidth : 600;
            let desiredHeight = showDialogOptions.desiredHeight ? showDialogOptions.desiredHeight : 600;

            let width = Math.min(Math.round($(window).width() * 0.9), desiredWidth);
            let height = Math.min(Math.round($(window).height() * 0.9), desiredHeight);

            let dialogOptions: Solovis.UI.DialogOptions = {
                container: this._container,

                ownerDialog: showDialogOptions.ownerDialog,
                width: width,
                height: height,
                center: true,
                modal: true,
                autoOpen: true,
                actions: ["Close"],

                destroyOnClose: true,                  

                contentWidthSizer: {
                    setWidth: function (newWidth) {
                        self.setWidth(newWidth);
                    }
                }           
            };            

            if (typeof showDialogOptions.modal !== "undefined") {
                dialogOptions.modal = showDialogOptions.modal;
            }

            if (showDialogOptions.title) {
                dialogOptions.title = showDialogOptions.title;
            }

            let buttons = [];

            if (showDialogOptions.buttons) {
                buttons = buttons.concat(showDialogOptions.buttons);
            }            

            if (showDialogOptions.showSaveCancel) {
                let saveButton: DialogButtonOptions = {
                    label: showDialogOptions.saveButtonText ? showDialogOptions.saveButtonText : "Save",
                    click: function () {
                        if (showDialogOptions.saveCallback) {
                            if (showDialogOptions.saveCallback()) {
                                dialog.close();
                            }
                        }
                    }
                };                

                buttons.push(saveButton);

                let cancelButton: DialogButtonOptions = {
                    label: "Cancel",
                    click: function () {
                        dialog.close();
                    }
                };

                buttons.push(cancelButton);
            }

            if (buttons.length) {
                dialogOptions.buttons = buttons;
            }

            let dialog = new Solovis.UI.Dialog(dialogOptions);

            return dialog;
        }


        public static findFormBuilderWithinElement(container): Solovis.UI.InputControls.FormBuilder {
            var firstFormBuilderContainer = $("div.formbuilder-form", container).parent().get(0);
            if (firstFormBuilderContainer && (firstFormBuilderContainer as any).__solovisFormBuilder) {
                return (firstFormBuilderContainer as any).__solovisFormBuilder as Solovis.UI.InputControls.FormBuilder;
            }

            return null;
      }

      public requiredControlHasValue(controlName: string): boolean {
        let controlValue = this.getValueByName(controlName);
        let formEntry = this.getEntryByName(controlName);

        if (controlValue) {
          formEntry.validationModel.clearState();
          return true;
        } else {
          formEntry.validationModel.setErrorMessage("This is a required field.");
          return false;
        }
      }

    }

}
